/**
 * EASM API module - External Attack Surface Management
 *
 * Real per-domain split: contains the actual implementations of the EASM
 * domain API objects, extracted verbatim from the monolithic
 * frontend/src/services/api.ts. This file replaces the prior stub that
 * re-exported from `../api`.
 *
 * Domain objects:
 *   - easmApi             (assets, domains, certs, ports, vulns, fingerprint, passive-dns, reports)
 *   - exposureApi         (internet exposure, attack paths, toxic combos)
 *   - exposureWindowApi   (vulnerability exposure-window / SLA compliance)
 */
import { _unwrap, _unwrapWithOpts, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import { getCurrentTenantId } from '../../contexts/TenantContext';

export const easmApi = {
  // Aggregator
  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/dashboard'));
  },
  getAssets: async (params?: { type?: string; risk_level?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/assets', params));
  },
  getAsset: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/assets/${id}`));
  },
  scanAssets: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/scan'));
  },
  rescanAsset: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/easm/assets/${id}/rescan`));
  },
  getAssetHistory: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/assets/${id}/history`));
  },
  getServices: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/services'));
  },
  getHighRiskAssets: async (limit?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/high-risk', { limit }));
  },
  getShadowIT: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/shadow-it'));
  },
  getExpiringCerts: async (days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/expiring-certs', { days }));
  },
  getRiskPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/risk-posture'));
  },
  getChanges: async (days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/changes', { days }));
  },
  getTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/trends'));
  },

  // Domain Discovery
  getDomains: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domains', params));
  },
  getDomain: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/domains/${id}`));
  },
  getSubdomains: async (domainId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/domains/${domainId}/subdomains`));
  },
  getDomainWhois: async (domainId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/domains/${domainId}/whois`));
  },
  getDomainDns: async (domainId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/domains/${domainId}/dns`));
  },
  scanDomain: async (domain: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/domains/scan', { domain }));
  },
  getPhishingDetection: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domains/phishing'));
  },
  getDomainStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domains/stats'));
  },
  getTyposquatting: async (domain: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domains/typosquat', { domain }));
  },

  // Certificate Monitor
  getCertificates: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates', params));
  },
  getCertificate: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/certificates/${id}`));
  },
  getExpiringCertificates: async (days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/expiring', { days }));
  },
  getRevokedCertificates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/revoked'));
  },
  scanCertificate: async (domain: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/certificates/scan', { domain }));
  },
  getCertChain: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/certificates/${id}/chain`));
  },
  getCertTransparency: async (domain: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/transparency', { domain }));
  },
  getCertCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/compliance'));
  },

  // Port Scanner
  getPortScans: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/scans'));
  },
  getOpenPorts: async (params?: { target?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/open-ports', params));
  },
  getExposedServices: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/exposed'));
  },
  getPortAttackSurface: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/attack-surface'));
  },
  getHighRiskPorts: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/high-risk'));
  },
  getNetworkMap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/network-map'));
  },

  // Vulnerability Correlator
  getVulnerabilities: async (params?: { severity?: string; exploitable?: boolean; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities', params));
  },
  correlateVulnerabilities: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/vulnerabilities/correlate'));
  },
  getExploitableVulns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/exploitable'));
  },
  getVulnRiskMatrix: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/risk-matrix'));
  },
  getVulnTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/trends'));
  },

  // New Enhanced Endpoints
  getCloudCorrelation: async (params?: { cloud_provider?: string; is_managed?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/cloud-correlation', params));
  },
  getGeoDistribution: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/geo-distribution'));
  },
  getExposedCredentials: async (params?: { severity?: string; status?: string; source?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/exposed-credentials', params));
  },
  getAttackSurfaceScore: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/attack-surface-score'));
  },
  getTechnologyStack: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/technology-stack'));
  },
  getScanJobs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/scan/jobs'));
  },
  getDanglingDns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domains/dangling-dns'));
  },
  getSubdomainTakeover: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/subdomain-takeover'));
  },
  getTypoSquatting: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/typosquatting', params));
  },
  detectTypoSquatting: async (data: { domain: string; variations?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/typosquatting/detect', data));
  },
  getPhishing: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/phishing', params));
  },
  detectPhishing: async (data: { domain: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/phishing/detect', data));
  },
  getDomainIntelligence: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/domain-intelligence'));
  },
  getSslGrades: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/ssl-grades'));
  },
  getHstsStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/hsts-status'));
  },
  getProtocolMatrix: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/certificates/protocol-matrix'));
  },
  getExposedServicesDetailed: async (params?: { category?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/exposed-services', params));
  },
  getWebApps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/web-apps'));
  },
  getNetworkTopology: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/ports/network-topology'));
  },
  getEpssScores: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/epss'));
  },
  getRemediationSla: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/remediation-sla'));
  },
  getVulnTimeline: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/vulnerabilities/timeline'));
  },

  // Technology Fingerprinting (port-scanner service)
  detectCms: async (data: { target: string; deep_scan?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/fingerprint/cms-detection', data));
  },
  detectJsLibraries: async (data: { target: string; check_vulnerabilities?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/fingerprint/js-libraries', data));
  },
  detectGraphql: async (data: { target: string; common_paths?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/fingerprint/graphql-introspection', data));
  },
  getTechnologyStackForAsset: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/fingerprint/technology-stack/${assetId}`));
  },
  getOutdatedTechnologies: async (params?: { severity?: string; category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/fingerprint/outdated-technologies', params));
  },

  // Passive DNS (domain-discovery service)
  getPassiveDns: async (domain: string, params?: { record_type?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/passive-dns/${domain}`, params));
  },
  getPassiveDnsChanges: async (params?: { domain?: string; significance?: string; days?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/passive-dns/changes', params));
  },
  getPassiveDnsNewSubdomains: async (params?: { days?: number; risk_level?: string; investigated?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/passive-dns/new-subdomains', params));
  },

  // Reporting (aggregator service)
  generateReport: async (data: { title?: string; report_type?: string; format?: string; sections?: string[]; date_range_days?: number; include_remediation?: boolean; recipients?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/reports/generate', data));
  },
  getReports: async (params?: { report_type?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/reports', params));
  },
  getReport: async (reportId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/easm/reports/${reportId}`));
  },
  scheduleReport: async (data: { title: string; report_type?: string; format?: string; frequency?: string; recipients?: string[]; enabled?: boolean; day_of_week?: number; day_of_month?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/easm/reports/schedule', data));
  },
  getReportTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/easm/reports/templates'));
  },
};

export const exposureApi = {
  // ── Dashboard & Stats ────────────────────────────────────────────────────
  getDashboard: async (tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/dashboard', {
      method: 'GET',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  getStatistics: async (tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/statistics', {
      method: 'GET',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  // ── Assets CRUD ──────────────────────────────────────────────────────────
  getInternetFacingAssets: async (params?: { severity?: string; provider?: string; sort?: string; page?: number; limit?: number }, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/assets', {
      method: 'GET',
      params,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  getAssetDetail: async (assetId: string, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/assets/${assetId}`, {
      method: 'GET',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  createAsset: async (data: any, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/assets', {
      method: 'POST',
      body: data,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  updateAsset: async (assetId: string, data: any, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/assets/${assetId}`, {
      method: 'PATCH',
      body: data,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  deleteAsset: async (assetId: string, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/assets/${assetId}`, {
      method: 'DELETE',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  // ── Attack Paths CRUD ────────────────────────────────────────────────────
  getAttackPaths: async (params?: { severity?: string; limit?: number }, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/attack-paths', {
      method: 'GET',
      params,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  getAttackPathDetail: async (pathId: string, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/attack-paths/${pathId}`, {
      method: 'GET',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  createAttackPath: async (data: any, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/attack-paths', {
      method: 'POST',
      body: data,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  deleteAttackPath: async (pathId: string, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/attack-paths/${pathId}`, {
      method: 'DELETE',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  // ── Toxic Combinations ───────────────────────────────────────────────────
  getToxicCombinations: async (params?: { limit?: number }, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/toxic-combinations', {
      method: 'GET',
      params,
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  deleteToxicCombination: async (tcId: string, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/api/v1/exposure/toxic-combinations/${tcId}`, {
      method: 'DELETE',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  // ── Scan & Paths ─────────────────────────────────────────────────────────
  triggerScan: async (tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/scan', {
      method: 'POST',
      body: {},
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },

  getPaths: async (tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/api/v1/exposure/paths', {
      method: 'GET',
      headers: { 'X-Tenant-Id': tenantId },
    }));
  },
};



export const exposureWindowApi = {
  list: async (params?: { severity?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exposure-windows', params));
  },
  get: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/exposure-window`));
  },
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exposure-windows/statistics'));
  },
  getSLACompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exposure-windows/sla-compliance'));
  },
};

// ── R138 / G-NETREACH: Network Reachability API ─────────────────────────
// Wraps the cspm-network-reachability child (mounted at /api/v1/network/* on
// cspm umbrella 8013, gateway routed via service_registry per-path entries).
// Wiz parity: deep SG+NACL+route-table+IGW+TGW+VPC-peering rule chaining
// produces effective reachability + lateral-movement + data-exfil-risk +
// what-if scenarios — far beyond the graph-engine's neighborhood walk.
export const networkReachabilityApi = {
  // POST /api/v1/network/analyze — kick analysis for a set of resources
  analyze: async (data: { tenant_id?: string; resource_ids?: string[]; scope?: string; force?: boolean } = {}): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/network/analyze', data));
  },
  // GET /api/v1/network/reachability/{resource_id} — per-resource reachability
  // NOTE: graph-engine ALSO owns this path (live Neo4j walk). Gateway routes
  // this to graph-engine; for the cspm pre-computed version use /effective-rules below.
  getReachability: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/network/reachability/${resourceId}`));
  },
  // GET /api/v1/network/internet-exposed — paginated internet-reachable resources
  listInternetExposed: async (params?: { limit?: number; offset?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/network/internet-exposed', params as any));
  },
  // GET /api/v1/network/paths — paths between source + target (or all)
  listPaths: async (params?: { source_id?: string; target_id?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/network/paths', params as any));
  },
  // POST /api/v1/network/paths/check — check whether a specific path is reachable
  checkPath: async (data: { source_id: string; target_id: string; port?: number; protocol?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/network/paths/check', data));
  },
  // GET /api/v1/network/topology/{vpc_id} — topology under a VPC
  getTopology: async (vpcId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/network/topology/${vpcId}`));
  },
  // GET /api/v1/network/exposure-report — tenant-wide exposure summary
  getExposureReport: async (params?: { severity?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/network/exposure-report', params as any));
  },
  // GET /api/v1/network/statistics — engine stats (analyses, paths, results)
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/network/statistics'));
  },
  // GET /api/v1/network/lateral-movement/{resource_id} — what can reach FROM this
  getLateralMovement: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/network/lateral-movement/${resourceId}`));
  },
  // GET /api/v1/network/data-exfiltration-risk — outbound exposure scoring
  getDataExfiltrationRisk: async (params?: { resource_id?: string; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/network/data-exfiltration-risk', params as any));
  },
  // GET /api/v1/network/effective-rules/{resource_id} — flattened SG+NACL ruleset
  getEffectiveRules: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/network/effective-rules/${resourceId}`));
  },
  // POST /api/v1/network/what-if — simulate adding/removing a rule
  whatIf: async (data: { resource_id: string; rule_change: any; scenario_name?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/network/what-if', data));
  },
};
